import { getEffectiveRole, isOrganizationAdminRole, isOrganizerRole, normalizeRole } from '@/lib/rbac';
import type { User } from '@/lib/auth-store';

export type OrganizationAccessUser =
  | Partial<Pick<User, 'role' | 'user_type' | 'roles' | 'has_organization_access' | 'has_developer_access'>>
  | null
  | undefined;

/** Organization staff: admin or manager with org dashboard access. */
export function isOrganizationStaffRole(role: unknown): boolean {
  const normalized = normalizeRole(role);
  return normalized === 'organization_admin' || normalized === 'organization_manager';
}

export function isOrganizationManagerRole(role: unknown): boolean {
  return normalizeRole(role) === 'organization_manager';
}

export function canAccessOrganizationDashboard(user: OrganizationAccessUser): boolean {
  if (!user) return false;
  const role = getEffectiveRole(user);
  return isOrganizationStaffRole(role) || Boolean(user.has_organization_access);
}

/** Nav item keys for organization sidebar permission filtering. */
export type OrganizationNavKey =
  | 'dashboard'
  | 'events'
  | 'create_event'
  | 'organizers'
  | 'organizer_requests'
  | 'team'
  | 'roles'
  | 'assignments'
  | 'analytics'
  | 'calendar'
  | 'finance'
  | 'payouts'
  | 'endorsements'
  | 'notifications'
  | 'messages'
  | 'support'
  | 'profile'
  | 'settings'
  | 'developer'
  | 'edop'
  | 'edop_documents'
  | 'edop_branding'
  | 'forms';

const ORG_ADMIN_ONLY: OrganizationNavKey[] = [
  'organizers',
  'organizer_requests',
  'team',
  'roles',
  'finance',
  'payouts',
  'endorsements',
  'profile',
  'settings',
  'developer',
  'edop_branding',
];

const ORG_MANAGER_ALLOWED: OrganizationNavKey[] = [
  'dashboard',
  'events',
  'create_event',
  'assignments',
  'analytics',
  'calendar',
  'notifications',
  'messages',
  'support',
  'edop',
  'edop_documents',
  'forms',
];

const ORG_ORGANIZER_ALLOWED: OrganizationNavKey[] = [
  'dashboard',
  'events',
  'create_event',
  'assignments',
  'analytics',
  'calendar',
  'notifications',
  'messages',
  'support',
  'forms',
];

export function canAccessOrganizationNavItem(
  user: OrganizationAccessUser,
  key: OrganizationNavKey
): boolean {
  if (!canAccessOrganizationDashboard(user)) return false;
  if (key === 'developer' && !user?.has_developer_access) return false;
  const role = getEffectiveRole(user);
  if (isOrganizationAdminRole(role)) return true;
  if (isOrganizationManagerRole(role)) {
    return ORG_MANAGER_ALLOWED.includes(key);
  }
  if (isOrganizerRole(role)) {
    return ORG_ORGANIZER_ALLOWED.includes(key);
  }
  return false;
}

/** Route paths restricted to organization_admin only (managers → 403). */
const ORG_ADMIN_ONLY_PATH_PREFIXES = [
  '/dashboard/organization/organizers',
  '/dashboard/organization/organizer-requests',
  '/dashboard/organization/team',
  '/dashboard/organization/roles',
  '/dashboard/organization/finance',
  '/dashboard/organization/profile',
  '/dashboard/organization/settings',
  '/dashboard/developer',
  '/dashboard/endorsements',
];

export function isOrganizationAdminOnlyPath(path: string): boolean {
  const normalized = path.split('?')[0].replace(/\/$/, '') || '/';
  return ORG_ADMIN_ONLY_PATH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
}

export function canAccessOrganizationPath(user: OrganizationAccessUser, path: string): boolean {
  if (!canAccessOrganizationDashboard(user)) return false;
  if ((path === '/dashboard/developer' || path.startsWith('/dashboard/developer/')) && !user?.has_developer_access) {
    return false;
  }
  const role = getEffectiveRole(user);
  if (isOrganizationAdminRole(role)) return true;
  if (isOrganizationManagerRole(role)) {
    if (isOrganizationAdminOnlyPath(path)) return false;
    if (path.startsWith('/dashboard/organization') || path === '/dashboard/calendar') return true;
    if (path.startsWith('/dashboard/organization/analytics')) return true;
    if (path.startsWith('/dashboard/events')) return true;
    if (path.startsWith('/dashboard/forms')) return true;
    if (path === '/dashboard/notifications' || path === '/dashboard/support' || path === '/dashboard/organization/messages') return true;
    return false;
  }
  if (isOrganizerRole(role)) {
    if (isOrganizationAdminOnlyPath(path)) return false;
    if (path === '/dashboard/organization' || path.startsWith('/dashboard/organization/events')) return true;
    if (path.startsWith('/dashboard/organization/assignments')) return true;
    if (path.startsWith('/dashboard/organization/analytics')) return true;
    if (path.startsWith('/dashboard/events')) return true;
    if (path.startsWith('/dashboard/forms')) return true;
    if (path === '/dashboard/notifications' || path === '/dashboard/support' || path === '/dashboard/organization/messages' || path === '/dashboard/calendar') return true;
    return false;
  }
  return false;
}
